// Per-chat registry of row-shaped tool results, keyed by the
// tool_call_id the agent already sees in its message history.
// Lets `execute_python` reference prior results via
// `args.from_tool_calls` instead of forcing the agent to re-emit
// the rows in `args.data`. Output tokens are ~5× input cost per
// token and don't benefit from prompt caching, so saving the
// rewrite-rows step is the high-value side of this optimisation.
//
// Scope is deliberately narrow:
//   • Only tools that produce row-shaped data write here
//     (analytics_query today; execute_python when it returns a
//     list of dicts).
//   • Only tools that take row-shaped data resolve from here
//     (execute_python via `from_tool_calls`).
//   • Tool call ids are stable, opaque strings the agent already
//     reads in its prior tool_use blocks — there's no new
//     namespace or q1/q2 numbering scheme to learn. Referencing a
//     stored result is just naming a thing the agent can already
//     see.
//
// Persistence (Phase 2): the store is now mirrored into IndexedDB
// via chatStorage. Small entries (≤ PREVIEW_ROW_THRESHOLD rows and
// ≤ SMALL_ENTRY_MAX_BYTES) are inlined in the chat record. Large
// entries go into the separate `largeRows` object store. Eviction triggers (one or both
// depending on context):
//   • on every transformContext pass, drop entries whose toolCallId
//     no longer appears in the agent's RAW history (fork, rewind, or a
//     manual /compact really removed the message, so nothing can
//     reference the rows again)
//   • when total large-entry size exceeds the per-chat byte budget,
//     drop oldest large entries by recordedAt — these leave behind
//     a marker `{ evicted: true, rows: null, source, recordedAt }`
//     so Phase 3 rehydration can distinguish "used to exist" from
//     "never existed."

import { onAuthIdentityChange } from "./authTokenStore.js";

const _stores = new Map();  // namespaceKey → Map<toolCallId, entry>
onAuthIdentityChange(() => _stores.clear());

// Default split: results with ≤ this many rows are inlined in the
// chat record on persist; larger results go to the `largeRows` IDB
// store. Matches analyticsTool.js's PREVIEW_ROW_THRESHOLD so the
// LLM-visible "small/inlined" notion is the same notion driving
// persistence layout.
const DEFAULT_SMALL_THRESHOLD = 100;

// Default per-chat budget for large entries. When total estimated
// size of large entries in one namespace exceeds this, the oldest
// large entries get their `rows` evicted (marker stays). 25 MB is
// "comfortable middle" across browser quotas.
const DEFAULT_BUDGET_BYTES = 25 * 1024 * 1024;

// Row count alone can't classify an entry: a handful of raw EVENT
// rows can carry megabytes of api_request payload each. Anything
// whose estimated bytes exceed this is "large" regardless of row
// count — kept out of the inlined chat record and subject to the
// byte-budget eviction above.
const SMALL_ENTRY_MAX_BYTES = 256 * 1024;

function entryIsSmall(entry, smallThreshold) {
    return Array.isArray(entry.rows)
        && entry.rows.length <= smallThreshold
        && (entry.sizeBytes || 0) <= SMALL_ENTRY_MAX_BYTES;
}

function getStore(namespaceKey) {
    const key = namespaceKey || "default";
    let store = _stores.get(key);
    if (!store) {
        store = new Map();
        _stores.set(key, store);
    }
    return store;
}

// Cheap sample-and-multiply estimate of row payload size in bytes
// (UTF-16 char count for ASCII data, approximately). Pays O(1) on
// record — stringifies one sample row, multiplies by count. Over /
// under-estimates by a factor when rows are highly heterogeneous,
// but the budget is "soft" and the cheap estimate is close enough.
function estimateRowsBytes(rows) {
    if (!Array.isArray(rows) || rows.length === 0) return 0;
    let sample = 64;
    if (rows[0]) {
        try { sample = JSON.stringify(rows[0]).length; }
        catch { sample = 64; }
    }
    return sample * rows.length;
}

// `source` is the producing tool's name — surfaced in the UI's
// resolved-inputs view so the user can see at a glance where a
// referenced result came from. `rows` is the canonical array of
// row dicts (analytics_query rows come pre-flattened via
// rowsToObjects; execute_python results are already this shape
// when row-like).
export function recordResult(namespaceKey, toolCallId, rows, source) {
    if (!toolCallId || !Array.isArray(rows)) return;
    const store = getStore(namespaceKey);
    store.set(toolCallId, {
        rows,
        source: source || "tool",
        recordedAt: Date.now(),
        sizeBytes: estimateRowsBytes(rows),
    });
}

// Record the "this used to exist" marker directly, without ever having
// held the rows. Same shape evictLargeBeyondBudget leaves behind, so
// every consumer (from_tool_calls, expand_preview, the rehydrator)
// already handles it.
//
// The case this exists for is a chat rehydrated onto a device that never
// ran it: the row payloads are local cache and stayed on the original
// browser, so its tool-call ids are referenced by history that this
// process has no rows for. A marker makes that an honest, recoverable
// gap instead of an "unknown id" the model can only retry.
//
// Never overwrites an existing entry - a real result already present
// locally outranks a marker.
export function recordEvictedMarker(namespaceKey, toolCallId, source) {
    if (!toolCallId) return false;
    const store = getStore(namespaceKey);
    if (store.has(toolCallId)) return false;
    store.set(toolCallId, {
        rows: null,
        evicted: true,
        source: source || "tool",
        recordedAt: Date.now(),
        sizeBytes: 0,
    });
    return true;
}

export function getResult(namespaceKey, toolCallId) {
    if (!toolCallId) return null;
    const store = _stores.get(namespaceKey || "default");
    if (!store) return null;
    return store.get(toolCallId) || null;
}

export function clearStore(namespaceKey) {
    _stores.delete(namespaceKey || "default");
}

// Copy every entry from one namespace into another. Used by
// forkFromTurn so the new chat inherits the parent's row payloads
// at fork-time — otherwise the first `from_tool_calls` in the fork
// would have to go through lazy rehydration (slow, hits the
// network). Shallow per-entry copy: `entry.rows` is shared by
// reference since recordResult never mutates an existing entry in
// place (it replaces). Evicted markers come along too — the fork
// inherits the same "used to exist" gap shape, and Phase 3
// rehydration still kicks in if the LLM references those.
export function cloneStore(srcNamespaceKey, dstNamespaceKey) {
    const src = _stores.get(srcNamespaceKey || "default");
    if (!src || src.size === 0) return;
    const dst = getStore(dstNamespaceKey);
    for (const [id, entry] of src) {
        dst.set(id, { ...entry });
    }
}

// Compact catalog of currently-stored results, newest first. Used
// by execute_python's failure path to make a "no result stored
// under this id" error self-correcting — the next turn sees the
// actual available ids and can re-emit with the right one.
// Capped to N most-recent entries so the error message stays
// readable on long chats. Excludes evicted markers (caller wants
// fetchable ids).
export function listAvailable(namespaceKey, { limit = 8 } = {}) {
    const store = _stores.get(namespaceKey || "default");
    if (!store) return [];
    const entries = [];
    for (const [id, val] of store) {
        if (!val || val.evicted) continue;
        entries.push({
            id,
            source: val.source,
            rowCount: Array.isArray(val.rows) ? val.rows.length : 0,
            recordedAt: val.recordedAt,
        });
    }
    entries.sort((a, b) => (b.recordedAt || 0) - (a.recordedAt || 0));
    return entries.slice(0, limit);
}

// Iterate live row-bearing entries, newest first, WITHOUT copying.
// `snapshotForPersistence` below shallow-copies every entry, so it is the wrong
// tool for a scan that re-runs as the transcript changes. `rows` is by
// reference and must not be mutated.
export function forEachResult(namespaceKey, fn) {
    const store = _stores.get(namespaceKey || "default");
    if (!store) return;
    const entries = [];
    for (const [id, entry] of store) {
        if (!entry || entry.evicted || !Array.isArray(entry.rows)) continue;
        entries.push([id, entry]);
    }
    entries.sort((a, b) => (b[1].recordedAt || 0) - (a[1].recordedAt || 0));
    for (const [id, entry] of entries) fn(entry.rows, id, entry);
}

// ── persistence helpers ───────────────────────────────────────────

// Snapshot the namespace's contents for persistence, split by row
// count and estimated bytes. Small entries are inlined in the chat
// record; large entries are written to the largeRows object store.
// Evicted
// markers stay with the small set (they're tiny and the chat
// record always needs them for accurate rehydration).
export function snapshotForPersistence(namespaceKey, { smallThreshold = DEFAULT_SMALL_THRESHOLD } = {}) {
    const store = _stores.get(namespaceKey || "default");
    if (!store) return { small: {}, large: [] };
    const small = {};
    const large = [];
    for (const [id, entry] of store) {
        if (!entry) continue;
        // Evicted markers (rows: null) are tiny — keep with small.
        if (entry.evicted || !Array.isArray(entry.rows)) {
            small[id] = { ...entry };
            continue;
        }
        if (entryIsSmall(entry, smallThreshold)) {
            small[id] = { ...entry };
        } else {
            large.push({ id, entry: { ...entry } });
        }
    }
    return { small, large };
}

// Replace the namespace's contents with hydrated data. Used on
// chat hydrate to restore both small entries (from the chat
// record) and large entries (from the largeRows IDB store).
export function loadStore(namespaceKey, { small, large } = {}) {
    const store = getStore(namespaceKey);
    store.clear();
    if (small && typeof small === "object") {
        for (const [id, entry] of Object.entries(small)) {
            if (entry && typeof entry === "object") {
                store.set(id, entry);
            }
        }
    }
    if (Array.isArray(large)) {
        for (const item of large) {
            if (item?.id && item?.entry && typeof item.entry === "object") {
                store.set(item.id, item.entry);
            }
        }
    }
}

// ── eviction ──────────────────────────────────────────────────────

// Drop entries whose toolCallId no longer appears anywhere in
// `messages`. Called with the agent's RAW history on every context
// transform so the store stays bounded by what can still be referenced;
// the compaction digest keeps tool_call_ids, so a folded result stays
// reachable.
// Returns evicted ids.
//
// Synthetic staged-input ids (`${spawnToolCallId}__staged__${var}`)
// are NEVER in the parent's messages (they live only in the child's
// initial messages). Without protection they'd be evicted on every
// parent transformContext pass — which breaks backgrounded children
// after an interrupted spawn. Keep them as long as the owning spawn
// toolCallId is still in the parent's messages; once compaction
// drops the spawn from history, eviction takes the staged ids along
// with it.
export function evictMissingFromMessages(namespaceKey, messages) {
    const store = _stores.get(namespaceKey || "default");
    if (!store) return [];
    const referenced = collectToolCallIds(messages);
    // Extend the "referenced" set with every id the harness recorded
    // under a call that is in messages: staged inputs
    // (`<owner>__staged__<var>`, buildStagedInputs in subAgentTools.js)
    // and the per-step results of the moves the harness runs itself
    // (`<owner>:<n>` and `<owner>.<i>:<n>` for expand and expand_all,
    // `<owner>:s<n>` for a recipe's queries, `<owner>:e<n>.<j>` and
    // `<owner>:e<n>.<j>:<k>` for the expands inside a recipe). A tool
    // call id itself carries no `:` or `.`, so the owner is whatever comes
    // before the first of them. They live as long as the call that
    // produced them, which is what the model is told when it is given
    // their ids.
    for (const id of store.keys()) {
        const owner = id.split(/__staged__|[.:]/)[0];
        if (owner !== id && referenced.has(owner)) {
            referenced.add(id);
        }
    }
    const evicted = [];
    for (const id of [...store.keys()]) {
        if (!referenced.has(id)) {
            store.delete(id);
            evicted.push(id);
        }
    }
    return evicted;
}

function collectToolCallIds(messages) {
    const ids = new Set();
    if (!Array.isArray(messages)) return ids;
    for (const m of messages) {
        if (!m) continue;
        if (m.role === "assistant" && Array.isArray(m.content)) {
            for (const c of m.content) {
                if (c?.type === "toolCall" && c.id) ids.add(c.id);
            }
        }
        if (m.role === "toolResult" && m.toolCallId) ids.add(m.toolCallId);
    }
    return ids;
}

// LRU eviction of LARGE entries when total estimated size exceeds
// `budgetBytes`. Small entries (≤ smallThreshold rows AND ≤
// SMALL_ENTRY_MAX_BYTES) are exempt — they're cheap and stay inline
// in the chat record. Evicted
// entries leave a marker `{ rows: null, evicted: true, source,
// recordedAt }` so future from_tool_calls / expand_preview can
// detect "this used to exist" and Phase 3 can rehydrate. Returns
// evicted ids.
export function evictLargeBeyondBudget(namespaceKey, {
    smallThreshold = DEFAULT_SMALL_THRESHOLD,
    budgetBytes = DEFAULT_BUDGET_BYTES,
} = {}) {
    const store = _stores.get(namespaceKey || "default");
    if (!store) return [];
    const large = [];
    let total = 0;
    for (const [id, entry] of store) {
        if (!entry || entry.evicted || !Array.isArray(entry.rows)) continue;
        if (entryIsSmall(entry, smallThreshold)) continue;
        const size = entry.sizeBytes || 0;
        large.push({ id, entry, size });
        total += size;
    }
    if (total <= budgetBytes) return [];
    large.sort((a, b) => (a.entry.recordedAt || 0) - (b.entry.recordedAt || 0));
    const evicted = [];
    for (const { id, entry } of large) {
        if (total <= budgetBytes) break;
        store.set(id, {
            rows: null,
            evicted: true,
            source: entry.source,
            recordedAt: entry.recordedAt,
            sizeBytes: 0,
        });
        evicted.push(id);
        total -= entry.sizeBytes || 0;
    }
    return evicted;
}
